import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useInterval } from 'react-use';

import { useAppSelector } from '@/hooks/redux-hooks';
import { useImpersonatorEmailFromStorage } from '@/services/storage';
import { SalesChannel, UserAccess } from '@/types/auth';
import type { TimeDifference } from '@/utils/getTimeDifference';
import getTimeDifference from '@/utils/getTimeDifference';
import isDateExpired from '@/utils/isDateExpired';

dayjs.extend(localizedFormat);

const useUserDetails = () => {
  const { userEmail, hsObjectId, subscription, terms } = useAppSelector(state => state.tripToken);
  const impersonatorEmail = useImpersonatorEmailFromStorage();

  const isUserPremium = useMemo(
    () => (subscription ? subscription.plan === UserAccess.Premium : true),
    [subscription]
  );

  const isUserBasic = useMemo(
    () => (subscription ? subscription.plan !== UserAccess.Premium : true),
    [subscription]
  );

  const expiredDate = useMemo(() => {
    const expireAt = subscription?.expireAt;
    if (!expireAt) {
      return undefined;
    }
    return dayjs(expireAt.includes('Z') ? expireAt : `${expireAt}Z`);
  }, [subscription]);

  const isTrial = useMemo(() => {
    return subscription?.trial;
  }, [subscription]);

  const salesChannel = useMemo(() => {
    return subscription?.salesChannel ?? SalesChannel.Sales;
  }, [subscription]);

  const [isUserExpired, setIsUserExpired] = useState<boolean | undefined>();
  const [timeToExpire, setTimeToExpire] = useState<TimeDifference>();

  useEffect(() => {
    if (expiredDate) {
      setIsUserExpired(subscription?.status !== 'active' || isDateExpired(expiredDate));
      setTimeToExpire(getTimeDifference(expiredDate));
    }
  }, [expiredDate, subscription]);

  useInterval(() => {
    if (expiredDate) {
      setIsUserExpired(subscription?.status !== 'active' || isDateExpired(expiredDate));
      setTimeToExpire(getTimeDifference(expiredDate));
    }
  }, 1000 * 60);

  const currentSubscription = useMemo(() => {
    if (isUserExpired) {
      return 'n/a';
    }
    let base = subscription?.plan ?? 'n/a';

    if (subscription?.trial) {
      base = `${base} Trial`;
    }
    return base;
  }, [isUserExpired, subscription]);

  const formatedExpirationDate = useMemo(() => {
    if (expiredDate) {
      return expiredDate.format('LLLL');
    }
    return undefined;
  }, [expiredDate]);

  return {
    status: subscription?.status,
    userEmail,
    hsObjectId,
    isUserBasic,
    isUserPremium,
    isUserExpired,
    isTrial,
    timeToExpire,
    currentSubscription,
    formatedExpirationDate,
    salesChannel,
    expiredDate,
    terms,
    impersonatorEmail
  };
};

export default useUserDetails;
